import { MainApi } from 'api/MainApi'
import { MaintenancePointApi } from 'api/MaintenancePointApi'
import { RobotApi } from 'api/RobotApi'
import { StationApi } from 'api/StationApi'
import Loader from 'components/Loader/Loader'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import {
  sortStationsForAssetsList,
  applyLiveStationSortFields,
  mergeStationsFromList,
} from 'pages/Assets/assetsStationsSort'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { PROGRESS_TYPES, ROBOT_NETWORK_STATUSES, TIMEOUT_FETCHING_STATUS } from 'utils/constants'
import { ILot, IMaintenancePoint, IRobotInfo, IStation } from 'utils/interfaces'
import { parseFacilityTab, serializeFacilityTab } from 'utils/urlViewParams'
import { filterRobotsByEnabledVendors } from 'utils/vendorDisplayName'

import FacilityView from './FacilityView/FacilityView'

const Facility = () => {
  const { id } = useParams<{ id: string }>()
  const facilityId = Number(id)
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [facility, setFacility] = useState<any>(null)
  const [robots, setRobots] = useState<IRobotInfo[]>([])
  const [stations, setStations] = useState<IStation[]>([])
  const [lots, setLots] = useState<ILot[]>([])
  const [maintenancePoints, setMaintenancePoints] = useState<IMaintenancePoint[]>([])
  const [url, setUrl] = useUrlQueryState({
    tab: {
      parse: parseFacilityTab,
      serialize: serializeFacilityTab,
    },
  })
  const activeTab = url.tab
  const setActiveTab = (tab: number) => setUrl({ tab })

  const isFetchingRef = useRef(false)

  const loadLotsForStations = async (stationList: IStation[]) => {
    if (stationList.length === 0) {
      setLots([])
      return
    }
    const stationIds = new Set(stationList.map(s => s.id))
    const allLots = await StationApi.fetchLots()
    setLots(allLots.filter(lot => stationIds.has(lot.robot_station)))
  }

  const fetchData = async (isInitial = false) => {
    if (isInitial && isFetchingRef.current) return
    isFetchingRef.current = true

    try {
      if (isInitial) {
        setLoadingProgress(PROGRESS_TYPES.WORK)

        const facilityData = await MainApi.fetchFacility(facilityId)
        setFacility(facilityData)

        const facilityRobotIds: number[] = facilityData?.robots_ids || []
        const [facilityRobots, stationsData, maintenancePointsData] = await Promise.all([
          facilityRobotIds.length > 0
            ? RobotApi.fetchRobots(undefined, undefined, undefined, facilityRobotIds)
            : Promise.resolve({ results: [], count: 0 }),
          StationApi.fetchStations({ facilityId }),
          MaintenancePointApi.fetchMaintenancePoints(facilityId),
        ])

        setStations(stationsData)
        setMaintenancePoints(maintenancePointsData)
        await loadLotsForStations(stationsData)

        setRobots(filterRobotsByEnabledVendors(facilityRobots.results))

        setLoadingProgress(PROGRESS_TYPES.SUCCESS)
      } else {
        setFacility((prevFacility: any) => {
          if (prevFacility) {
            const facilityRobotIds: number[] = prevFacility.robots_ids || []
            if (facilityRobotIds.length > 0) {
              RobotApi.fetchRobots(undefined, undefined, undefined, facilityRobotIds).then(facilityRobots => {
                setRobots(filterRobotsByEnabledVendors(facilityRobots.results))
              })
            }
            StationApi.fetchStations({ facilityId: prevFacility.id }).then(async stationsData => {
              setStations(prev => mergeStationsFromList(prev, stationsData))
              await loadLotsForStations(stationsData)
            })
            MaintenancePointApi.fetchMaintenancePoints(prevFacility.id).then(setMaintenancePoints)
          }
          return prevFacility
        })
      }
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        if (isInitial) setLoadingProgress(PROGRESS_TYPES.IDLE)
        return
      }
      if (isInitial) setLoadingProgress(PROGRESS_TYPES.ERROR)
      console.warn('[Facility] fetchData error:', error)
    } finally {
      isFetchingRef.current = false
    }
  }

  useEffect(() => {
    fetchData(true)
  }, [facilityId])

  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 1) {
        fetchData()
      }
    }, TIMEOUT_FETCHING_STATUS)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, activeTab])

  const handleLiveStationFields = useCallback((fieldsById: Record<number, Partial<IStation>>) => {
    setStations(prev => applyLiveStationSortFields(prev, fieldsById))
  }, [])

  if (loadingProgress === PROGRESS_TYPES.WORK) return <Loader />
  if (loadingProgress === PROGRESS_TYPES.ERROR) return <div>Error loading facility</div>
  if (!facility) return null

  return (
    <FacilityView
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      facility={facility}
      robots={robots.sort((a, b) => {
        if (a.networkStatus !== b.networkStatus) {
          return a.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE ? -1 : 1
        }
        const diagA = a.systemDiagnostics?.level ?? 0
        const diagB = b.systemDiagnostics?.level ?? 0
        if (diagA !== diagB) return diagB - diagA
        if (a.emergencyButton !== b.emergencyButton) return a.emergencyButton ? -1 : 1
        return a.serialNumber.localeCompare(b.serialNumber)
      })}
      stations={sortStationsForAssetsList(stations)}
      lots={lots}
      maintenancePoints={maintenancePoints}
      onRobotsUpdated={async () => {
        const facilityData = await MainApi.fetchFacility(facilityId)
        setFacility(facilityData)
        const facilityRobotIds: number[] = facilityData?.robots_ids || []
        if (facilityRobotIds.length > 0) {
          const facilityRobots = await RobotApi.fetchRobots(undefined, undefined, undefined, facilityRobotIds)
          setRobots(filterRobotsByEnabledVendors(facilityRobots.results))
        } else {
          setRobots([])
        }
      }}
      onStationsChanged={async () => {
        const stationsData = await StationApi.fetchStations({ facilityId })
        setStations(prev => mergeStationsFromList(prev, stationsData))
        await loadLotsForStations(stationsData)
      }}
      onLiveStationFields={handleLiveStationFields}
      onMaintenancePointsChanged={async () => {
        const points = await MaintenancePointApi.fetchMaintenancePoints(facilityId)
        setMaintenancePoints(points)
      }}
    />
  )
}

export default Facility
